/**
 * ActorNameContext — DRY actor ID → display name resolution.
 *
 * Single injection point: wraps the app once, all FieldValue instances
 * and Tabulator formatters resolve actor IDs via this context.
 *
 * Data source: useActorNames() (Apollo cache-first, single network request).
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useActorNames } from '@/hooks/useActorNames';

/**
 * Field names that contain actor IDs requiring name resolution.
 * Used by FieldValue (React context) and TabulatorPrimitiveManager (HTML formatter).
 *
 * Audit: verified against generated/graphql.ts — every field here exists
 * as an output field on at least one primitive type.
 */
export const ACTOR_ID_FIELDS: ReadonlySet<string> = new Set([
  // Core assignment / authorship
  'assignedActorId',
  'subjectActorId',
  'createdBy',
  'assignedBy',
  'approverId',
  // Task-specific (DRR-118 constellation roles)
  'auditorActorId',
  'authorityActorId',
  // Trace / audit
  'actorId',
  // Transaction
  'complianceOfficerId',
  // Resource ownership
  'ownerActorId',
  // Actor hierarchy
  'supervisedByActorId',
  // Checkpoint recovery
  'recoveredByActorId',
  // Reassignment / transfer
  'fromActorId',
  'toActorId',
  // Messaging / annotation
  'senderActorId',
  'authorActorId',
  // Elopement alert
  'subjectActorId',
  // Analytics (non-standard naming)
  'createdByUserId',
]);

type ActorNameResolver = (id: string | null | undefined) => string;

const ActorNameContext = createContext<ActorNameResolver>((id) => id ?? '\u2014');

export function ActorNameProvider({ children }: { children: ReactNode }) {
  const { resolveActorName } = useActorNames();
  return (
    <ActorNameContext.Provider value={resolveActorName}>
      {children}
    </ActorNameContext.Provider>
  );
}

/** Resolve an actor ID to display name via the nearest ActorNameProvider. */
export const useResolveActorName = (): ActorNameResolver => useContext(ActorNameContext);
